import styled from 'styled-components';
import { useMemo, useState } from 'react';

import { CATEGORY_LABEL_MAP } from 'search/enums/constants';
import { Caret } from 'shared/icons/Caret';
import { CheckboxRadio } from 'shared/components/CheckboxRadio';
import {
  transformCategoryFacet,
  getAllParents,
  getAllNestedSelectedChildrenValues,
} from 'search/utils/categoryFilterUtils';
import { getModalGridTemplateColumnStyle } from 'search/styles/SearchStyles';
import { getConstructorCategoriesStrings } from 'search/enums/translations';
import { variableFontRegular } from 'styles/theme/text';
import { IConstructorFacetMultiple } from 'shared/types/ConstructorFacet';
import { t } from 'localization/utils/t';
import { getTranslations } from 'shared/utils/getTranslations';

const TP = 'search.components.searchGrid.SearchFilterCategoryMobile';

const Checkbox = ({
  category,
  flatCategoryList,
  numColumns,
  nestedLevel = 0,
  selectedFilterValues,
  setSelectedFilterValues,
}) => {
  const categoryLabelMap = getTranslations(CATEGORY_LABEL_MAP);
  const handleChange = (option) => {
    const optionValue = option.value;
    const allNestedSelectedChildren = getAllNestedSelectedChildrenValues(
      option,
      selectedFilterValues,
    );
    const allParents = getAllParents({ option, flatCategoryList, selectedFilterValues });

    if (selectedFilterValues.includes(optionValue)) {
      const updatedValues = selectedFilterValues.filter((value) => {
        return value !== optionValue;
      });
      setSelectedFilterValues(updatedValues);
    } else if (selectedFilterValues.some((cat) => allParents.includes(cat))) {
      // if selected option's parent or any top parent is already selected(temporarily or currently), remove its parent
      const updatedWithParentRemoved = selectedFilterValues.filter(
        (cat) => !allParents.includes(cat),
      );
      setSelectedFilterValues([...updatedWithParentRemoved, optionValue]);
    } else if (selectedFilterValues.some((cat) => allNestedSelectedChildren.includes(cat))) {
      // if selected option's children are selected (currently or temporarily), remove all its children
      const allNestedChildrenRemoved = selectedFilterValues.filter(
        (cat) => !allNestedSelectedChildren.includes(cat),
      );
      setSelectedFilterValues([...allNestedChildrenRemoved, optionValue]);
    } else {
      setSelectedFilterValues([...selectedFilterValues, optionValue]);
    }
  };

  return (
    <Checkbox.Checkbox
      checked={Array.isArray(selectedFilterValues) && selectedFilterValues.includes(category.value)}
      data-qa="checkbox_option"
      label={
        categoryLabelMap[category.display_name] ||
        getConstructorCategoriesStrings(category.display_name)
      }
      name={category.value}
      onChange={() => handleChange(category)}
      $numColumns={numColumns}
      $nestedLevel={nestedLevel}
      type="checkbox"
    >
      <span data-qa="category_count">{category.count?.toLocaleString('en-US')}</span>
    </Checkbox.Checkbox>
  );
};

const CollapsibleRow = ({
  category,
  flatCategoryList,
  numColumns,
  nestedLevel = 0,
  selectedFilterValues,
  setSelectedFilterValues,
}) => {
  const hasSelectedNestedChildren =
    getAllNestedSelectedChildrenValues(category, selectedFilterValues)?.length > 0;
  const categoryIsSelected = category.status === 'selected';
  const [isExpanded, setIsExpanded] = useState(hasSelectedNestedChildren || categoryIsSelected);

  const toggleExpanded = () => setIsExpanded((prevState) => !prevState);

  return (
    <>
      <CollapsibleRow.CollapsibleRow $numColumns={numColumns} data-qa="category_collapsible_row">
        <div data-qa="category_name">{getConstructorCategoriesStrings(category.display_name)}</div>
        <span data-qa="category_count">{category.count?.toLocaleString('en-US')}</span>
        <Caret direction={isExpanded ? 'up' : 'down'} onClick={toggleExpanded} />
      </CollapsibleRow.CollapsibleRow>
      {isExpanded && category.children && (
        <>
          <Checkbox
            category={{
              ...category,
              display_name: t(`${TP}.all`, 'All {category}', {
                category: category.display_name as string,
              }),
            }}
            flatCategoryList={flatCategoryList}
            numColumns={numColumns}
            nestedLevel={nestedLevel + 1}
            selectedFilterValues={selectedFilterValues}
            setSelectedFilterValues={setSelectedFilterValues}
          />
          {category.children.map((cat) =>
            cat.children ? (
              <CollapsibleRow
                category={cat}
                flatCategoryList={flatCategoryList}
                key={cat.value}
                numColumns={numColumns}
                nestedLevel={nestedLevel + 1}
                selectedFilterValues={selectedFilterValues}
                setSelectedFilterValues={setSelectedFilterValues}
              />
            ) : (
              <Checkbox
                category={cat}
                flatCategoryList={flatCategoryList}
                key={cat.value}
                numColumns={numColumns}
                nestedLevel={nestedLevel + 1}
                selectedFilterValues={selectedFilterValues}
                setSelectedFilterValues={setSelectedFilterValues}
              />
            ),
          )}
        </>
      )}
    </>
  );
};

const SearchFilterCategoryCheckbox = ({
  category,
  flatCategoryList,
  numColumns,
  selectedFilterValues,
  setSelectedFilterValues,
}) => {
  return (
    <>
      <Checkbox
        category={category}
        flatCategoryList={flatCategoryList}
        numColumns={numColumns}
        selectedFilterValues={selectedFilterValues}
        setSelectedFilterValues={setSelectedFilterValues}
      />

      {category.children?.length > 0 &&
        category.children.map((cat) =>
          cat.children ? (
            <CollapsibleRow
              category={cat}
              flatCategoryList={flatCategoryList}
              key={cat.value}
              numColumns={numColumns}
              selectedFilterValues={selectedFilterValues}
              setSelectedFilterValues={setSelectedFilterValues}
            />
          ) : (
            <Checkbox
              category={cat}
              flatCategoryList={flatCategoryList}
              key={cat.value}
              numColumns={numColumns}
              selectedFilterValues={selectedFilterValues}
              setSelectedFilterValues={setSelectedFilterValues}
            />
          ),
        )}
    </>
  );
};

export interface ISearchFilterCategoryMobile {
  facet: IConstructorFacetMultiple;
  name?: string;
  numColumns: number;
  selectedFilterValues: Array<string>;
  setSelectedFilterValues: (options: Array<string>) => void;
  topLevelCategory: string;
}

export const SearchFilterCategoryMobile = ({
  facet,
  numColumns,
  selectedFilterValues = [],
  setSelectedFilterValues,
  topLevelCategory,
}: ISearchFilterCategoryMobile) => {
  const transformedCategories = useMemo(
    () => transformCategoryFacet(facet?.options, topLevelCategory),
    [JSON.stringify(facet?.options)],
  );

  return (
    <>
      {transformedCategories?.children &&
        transformedCategories.children.map((category) => (
          <SearchFilterCategoryCheckbox
            category={category}
            flatCategoryList={facet?.options}
            key={category.value}
            numColumns={numColumns}
            selectedFilterValues={selectedFilterValues}
            setSelectedFilterValues={setSelectedFilterValues}
          />
        ))}
    </>
  );
};

SearchFilterCategoryCheckbox.Collapsible = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  border: 1px solid blue;
`;

Checkbox.Checkbox = styled(CheckboxRadio)<{ $numColumns: number; $nestedLevel?: number }>`
  display: grid;
  grid-template-columns: ${({ $numColumns }) => getModalGridTemplateColumnStyle($numColumns)};
  column-gap: 8px;
  word-break: break-word;
  text-indent: ${({ $nestedLevel }) => `${$nestedLevel * 16}px`};
  > span {
    text-indent: 0;
  }
`;

CollapsibleRow.CollapsibleRow = styled.div<{ $numColumns: number }>`
  display: grid;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
  ${variableFontRegular({ size: '11.5px', height: '16px' })};
  grid-template-columns: ${({ $numColumns }) => getModalGridTemplateColumnStyle($numColumns)};
  column-gap: 8px;
  word-break: break-word;
`;
